/**
 * The offboarding material's link token and BankID session, in sessionStorage: one tab, gone when it is closed, as the
 * customer may be on a shared computer. The token comes from the link's fragment, so it never reaches a server log.
 */
export interface OffboardingMaterialSession {
  sessionToken: string;
  expiresAt: string;
  givenName?: string;
  surname?: string;
}

const LINK_KEY = "offboarding_material_link";
const SESSION_KEY = "offboarding_material_session";

const read = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // Without storage the page still works until it is reloaded.
  }
};

const OffboardingMaterialStorage = {
  getLinkToken: (): string | undefined => read(LINK_KEY) ?? undefined,

  /** A different link than the stored one ends the stored session, as it may be for another company. */
  setLinkToken: (linkToken: string) => {
    if (read(LINK_KEY) !== linkToken) write(SESSION_KEY, null);
    write(LINK_KEY, linkToken);
  },

  /** The session, or undefined once it has run out. */
  getSession: (): OffboardingMaterialSession | undefined => {
    const stored = read(SESSION_KEY);
    if (!stored) return undefined;
    try {
      const session = JSON.parse(stored) as OffboardingMaterialSession;
      return session.sessionToken && new Date(session.expiresAt).getTime() > Date.now() ? session : undefined;
    } catch {
      return undefined;
    }
  },

  /** Stores the session only while its link is still the stored one: a login that finishes after a new link was opened is not that link's. */
  setSession: (session: OffboardingMaterialSession, linkToken: string | undefined) => {
    if (linkToken && read(LINK_KEY) === linkToken) write(SESSION_KEY, JSON.stringify(session));
  },

  clearSession: () => write(SESSION_KEY, null),
};

export default OffboardingMaterialStorage;
